import { copyTextWithNotify } from "@/helpers/clipboard.helper";
import { apiOffers } from "@/api/recruit/offer.api.ts";
import { ApprovalsPersons } from "@/components/approvals/ApprovalsPersons.tsx";
import {
  ConfirmationModal,
  Input,
  Switch,
  useModals,
  useToasts,
  Modal,
  ModalProps,
  Avatar,
  Box,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSeparator,
  FontAwesomeIcon,
  ListItem,
  ListOption,
  Stack,
  Text,
  Title,
} from "@skailer-com/uikit";
import { useDownloadHelper } from "@/hooks/use-download.helper.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { OfferRequestViewModalSkeleton } from "@/pages/recruit/offers/components/OfferRequestViewModalSkeleton.tsx";
import { OfferViewFields } from "@/pages/recruit/offers/components/OfferViewFields.tsx";
import { OfferEditModal } from "@/pages/recruit/offers/widgets/OfferEditModal.tsx";
import {
  useOfferDelete,
  useOfferDetails,
  useOfferRequestAnswer,
  useOfferRequestCancel,
} from "@/queries/recruit/use-offer.query.ts";
import { useEntityFormConfig } from "@/queries/use-custom-fields.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiApprovalRequestStatus,
  getRequestStatusTitle,
} from "@/types/approvals/approval.types.ts";
import { ApiCFEntityTypes, ApiFieldTypes } from "@/types/custom-field.types.ts";
import { ApiOfferFileTypes } from "@/types/recruit/offer.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";

interface OfferRequestViewModalProps extends ModalProps {
  requestId?: number;
}

export function OfferRequestViewModal({ requestId, ...props }: OfferRequestViewModalProps) {
  const { t } = useTranslation();
  const { renderEmployeeName } = useFormatter();
  const config = useEntityFormConfig(ApiCFEntityTypes.offer);
  const { showInfo } = useToasts();
  const { close, openModal } = useModals();
  const { id } = props;
  const { isExporting, downloadFile } = useDownloadHelper();
  const { data: offerRequest, isLoading } = useOfferDetails(requestId);
  const answerRequest = useOfferRequestAnswer();
  const deleteOffer = useOfferDelete();
  const cancelOffer = useOfferRequestCancel();

  const { canIEdit } = usePermissions();
  const canManageRequests = canIEdit(ApiPermission.manage_offers);

  const [isApproveButtonShouldLoading, setIsApprove] = useState<boolean>(false);
  const [useComment, setUseComment] = useState<boolean>(false);
  const [comment, setComment] = useState<string>("");

  useEffectOnce(() => {
    if (offerRequest) {
      analyticsService.trackEvent(analyticEvents.offers.requestViewed, {
        [analyticProperties.id]: offerRequest.approval_request_details?.id,
        [analyticProperties.title]: "Job Offer",
        [analyticProperties.actionType]: "View",
      });
    }
  });

  const handleEditRequest = () => {
    close(id);

    if (!offerRequest) return;

    openModal(OfferEditModal, {
      offerRequest: offerRequest,
    });
  };

  const handleDownloadOffer = useCallback(() => {
    const offerId = offerRequest?.approval_request_details?.id;

    if (!offerId) return;

    downloadFile({
      apiCall: () => apiOffers.downloadOffer(offerId, ApiOfferFileTypes.PDF),
      onSuccess: () => {
        analyticsService.trackEvent(analyticEvents.offers.offerDownloaded, {
          [analyticProperties.id]: offerId,
        });
      },
    });
  }, [offerRequest?.approval_request_details?.id]);

  const handleAnswerRequest = async (approve: boolean, isBypassApprovalFlow: boolean = false) => {
    if (!offerRequest?.approval_request_details) return;

    setIsApprove(approve);

    const requestId = offerRequest.approval_request_details.id;
    const bypass = canManageRequests ? isBypassApprovalFlow : false;

    const payload = {
      id: requestId,
      approve,
      payload: { is_bypass_approval_flow: bypass, comment: useComment ? comment : null },
    };

    const trackAnalytics = () => {
      analyticsService.trackEvent(analyticEvents.offers.requestAction, {
        [analyticProperties.id]: requestId,
        [analyticProperties.title]: "Job Offer",
        [analyticProperties.actionType]: approve ? "Approve" : "Reject",
      });
    };

    if (canManageRequests && isBypassApprovalFlow) {
      openModal(ConfirmationModal, {
        question: t(approve ? "approval:approve_as_admin" : "approval:reject_as_admin"),
        text: t(
          approve ? "approval:approve_as_admin_description" : "approval:reject_as_admin_description"
        ),
        acceptTitle: t(approve ? "approval:approve" : "approval:reject"),
        onAccept: async function () {
          try {
            await answerRequest.mutateAsync(payload);
            showInfo({
              title: approve ? t("approval:request_approved") : t("approval:request_rejected"),
            });
            trackAnalytics();
          } catch {
            return;
          }
        },
      });
    } else {
      try {
        await answerRequest.mutateAsync(payload);
        trackAnalytics();
      } catch {
        return;
      }
    }
  };

  const handleCancel = () => {
    if (!offerRequest?.approval_request_details?.id) return;

    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("approval:cancel_request_confirm_description"),
      acceptTitle: t("common:yes"),
      onAccept: async function () {
        try {
          if (!offerRequest.approval_request_details) return;

          await cancelOffer.mutateAsync(offerRequest?.approval_request_details.id);

          showInfo({ title: t("approval:request_canceled") });

          analyticsService.trackEvent(analyticEvents.offers.requestAction, {
            [analyticProperties.id]: id,
            [analyticProperties.title]: "Job Offer",
            [analyticProperties.actionType]: "Cancel",
          });
        } catch {
          return;
        }
      },
    });
  };

  const handleDelete = () => {
    if (!offerRequest?.approval_request_details?.id) return;

    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("core:delete_request_confirm_description"),
      acceptTitle: t("common:yes"),
      onAccept: async function () {
        try {
          if (!offerRequest.approval_request_details) return;

          await deleteOffer.mutateAsync(offerRequest?.approval_request_details.id);

          showInfo({ title: t("approval:request_deleted") });

          analyticsService.trackEvent(analyticEvents.offers.requestAction, {
            [analyticProperties.id]: id,
            [analyticProperties.title]: "Job Offer",
            [analyticProperties.actionType]: "Delete",
          });

          close(id);
        } catch {
          return;
        }
      },
    });
  };

  const publicLink = useMemo(() => {
    if (offerRequest?.offer_data && offerRequest.offer_data.config.is_public) {
      const uuid = offerRequest.offer_data.uuid;
      const offerId = offerRequest.offer_data?.id;
      if (uuid && offerId) {
        return `${window.location.origin}/offers/public/${uuid}/${offerId}`;
      }
    }
    return "";
  }, [offerRequest]);

  const copyToClipboard = useCallback(() => {
    if (publicLink) {
      copyTextWithNotify(publicLink).then((copied) => {
        if (!copied) return;
        showInfo({ title: t("common:link_copied") });
      });
    }
  }, [publicLink]);

  return (
    <Modal
      title={t("ats:job_offer_request")}
      {...props}
      size="lg"
      actions={
        offerRequest?.acl.can_reject || offerRequest?.acl.can_approve ? (
          <Stack gap={"smd"} className={"pt-1"}>
            <Stack gap={"sm"}>
              <Switch
                size={"sm"}
                checked={useComment}
                onChange={setUseComment}
                title={t("common:add_comment")}
              />
              {useComment && (
                <Input
                  label={t("approval:comment_your_decision") + "..."}
                  type={"text"}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
              )}
            </Stack>
            <Stack gap={"sm"}>
              {offerRequest.acl.can_approve && (
                <Button
                  size={"lg"}
                  type={"submit"}
                  className={"w-full"}
                  isLoading={answerRequest.isPending && isApproveButtonShouldLoading}
                  onClick={() => {
                    void handleAnswerRequest(true);
                  }}
                >
                  {t("approval:approve")}
                </Button>
              )}
              {offerRequest.acl.can_reject && (
                <Button
                  variant={"secondary"}
                  size={"lg"}
                  type={"submit"}
                  className={"w-full"}
                  isLoading={answerRequest.isPending && !isApproveButtonShouldLoading}
                  onClick={() => {
                    void handleAnswerRequest(false);
                  }}
                >
                  {t("approval:reject")}
                </Button>
              )}
            </Stack>
          </Stack>
        ) : (
          <Button
            size={"lg"}
            className={"w-full"}
            variant={"secondary"}
            onClick={() => {
              close(id);
            }}
          >
            {t("common:close")}
          </Button>
        )
      }
      headerActionsSlot={(() => {
        const hasUpperMenuOptions =
          offerRequest?.acl.can_edit ||
          offerRequest?.acl.can_cancel ||
          offerRequest?.acl.can_bypass_flow;

        const canBypassFlow = canManageRequests && offerRequest?.acl.can_bypass_flow;

        const hasDeleteOption = offerRequest?.acl.can_delete;

        const shouldShowMenu = hasUpperMenuOptions || hasDeleteOption;

        const shouldShowDivider = hasUpperMenuOptions && hasDeleteOption;

        return shouldShowMenu ? (
          <DropdownMenu
            trigger={
              <Button
                variant={"secondary"}
                rightIcon={<FontAwesomeIcon icon={"fa-light fa-chevron-down"} />}
              >
                {t("common:actions")}
              </Button>
            }
          >
            {offerRequest.acl.can_edit && (
              <ListOption title={t("ats:edit_offer")} onClick={handleEditRequest} />
            )}
            {offerRequest.acl.can_cancel && (
              <ListOption title={t("common:cancel") + "..."} onClick={() => handleCancel()} />
            )}
            {canBypassFlow && (
              <DropdownMenuItem
                title={t("approval:approve_as_admin") + "..."}
                description={t("approval:approve_as_admin_description")}
                leftIcon={<FontAwesomeIcon icon="fa-light fa-crown text-warning" />}
                isSelected={false}
                onClick={() => handleAnswerRequest(true, true)}
              />
            )}
            {canBypassFlow && (
              <DropdownMenuItem
                title={t("approval:reject_as_admin") + "..."}
                description={t("approval:reject_as_admin_description")}
                leftIcon={<FontAwesomeIcon icon="fa-light fa-crown text-warning" />}
                isSelected={false}
                onClick={() => handleAnswerRequest(false, true)}
              />
            )}
            {shouldShowDivider && <DropdownMenuSeparator />}
            {hasDeleteOption && (
              <ListOption
                titleSlot={<Text className={"text-danger"}>{t("common:delete") + "..."}</Text>}
                onClick={() => handleDelete()}
              />
            )}
          </DropdownMenu>
        ) : (
          <></>
        );
      })()}
      withCloser
    >
      <Stack>
        {isLoading && <OfferRequestViewModalSkeleton />}

        {offerRequest && offerRequest.approval_request_details && (
          <Stack gap="md">
            <Stack direction={"horizontal"} gap={"md"} items={"center"}>
              <Avatar
                url={offerRequest.offer_data.applicant.photo_url}
                acronym={`${offerRequest.offer_data.first_name} ${offerRequest.offer_data.last_name}`}
                size={"xl"}
              />
              <Stack>
                <Title
                  header={`${offerRequest.offer_data.first_name} ${offerRequest.offer_data.last_name}`}
                  size="lg"
                  caption={
                    config.hasField(ApiFieldTypes.offer_job_title)
                      ? (offerRequest.offer_data.job_title ?? "")
                      : ""
                  }
                />
              </Stack>
            </Stack>
            <Title header={t("common:details")} size="xs" />
            <Stack>
              <ListItem
                preTitle={t("common:status")}
                titleSlot={
                  <Text
                    className={`font-semibold ${
                      offerRequest.approval_request_details.status ===
                      ApiApprovalRequestStatus.approved
                        ? "text-positive"
                        : offerRequest.approval_request_details.status ===
                            ApiApprovalRequestStatus.awaiting
                          ? "text-warning"
                          : "text-danger"
                    }`}
                  >
                    {getRequestStatusTitle(offerRequest.approval_request_details.status)}
                  </Text>
                }
              />
              {offerRequest.approval_request_details.created_by &&
                offerRequest.approval_request_details.created_by.id !==
                  offerRequest.approval_request_details.employee.id && (
                  <ListItem
                    preTitle={t("common:created_by")}
                    title={renderEmployeeName(offerRequest.approval_request_details.created_by)}
                  />
                )}
              <OfferViewFields offer={offerRequest.offer_data} />
            </Stack>

            <ApprovalsPersons
              request={offerRequest.approval_request_details}
              title={t("common:persons")}
            />

            {offerRequest.approval_request_details && (
              <Stack className="pt-4 pb-2 mt-2" gap="smd">
                <Title header={t("common:actions")} size="xs" />
                {offerRequest.offer_data.config.is_public && publicLink && (
                  <Box variant={"border"}>
                    <ListItem
                      className="items-center"
                      behavior={"card"}
                      preTitleSlot={
                        <Stack direction={"horizontal"} gap={"md"} className={"pl-0.5"}>
                          <Stack>
                            <Text className={"text-title"}>{t("common:shareable_link")}</Text>
                          </Stack>
                        </Stack>
                      }
                      valueSlot={
                        <Button
                          className={"shrink-0"}
                          variant="secondary"
                          size="sm"
                          onClick={copyToClipboard}
                          leftIcon={<FontAwesomeIcon icon="fa-light fa-link-simple" />}
                        >
                          {t("common:copy_link")}
                        </Button>
                      }
                    />
                  </Box>
                )}
                <Stack gap="sm">
                  <ListItem
                    title={t("common:save_as_pdf")}
                    valueSlot={
                      <Button
                        isLoading={isExporting}
                        variant={"secondary"}
                        onClick={() => handleDownloadOffer()}
                      >
                        {t("common:download_pdf")}
                      </Button>
                    }
                  />
                </Stack>
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}
